import {
  arrayUnion,
  collection,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {
  Company,
  CompanySettings,
  AllowedAuthMethod,
  CreateCompanyFromGetStartedWebDataProps,
  Department,
  FirestoreCollection,
  HRIS,
  IntegrationPlatform,
  User,
} from 'hubbl-shared';
import {CompanyDTO, UserDTO} from '../models';

import {firebaseFunctions, firestore} from './firebase';
import {mapCompanyDTOToCompany} from './utils/map-incoming-company/mapCompanyDTOToCompany';
import {mapUserDTOToUser} from './utils/map-incoming-users/mapUserDTOToUser';

const getLoggedInUserCompany = async (
  companyId: string,
): Promise<Company | null> => {
  const companyPromise = new Promise<Company | null>(
    async (resolve, reject) => {
      try {
        const companyRef = doc(
          firestore,
          FirestoreCollection.Companies,
          companyId,
        );
        const companySnapshot = await getDoc(companyRef);

        if (companySnapshot.exists()) {
          //TODO: PARSE CREATED AT LIKE IN APP
          resolve(mapCompanyDTOToCompany(companySnapshot.data() as CompanyDTO));
        } else {
          resolve(null);
        }
      } catch (error) {
        reject(error);
      }
    },
  );
  return companyPromise;
};

export const getCompanyEmployees = async (
  companyId: string,
  limitCount: number,
  lastVisible: DocumentSnapshot<User> | null,
): Promise<{
  employees: User[];
  lastVisibleSnapshot: DocumentSnapshot<User> | null;
}> => {
  try {
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
    const usersRef = collection(companyRef, FirestoreCollection.Users);

    // Start the query after the last visible document if it's provided (for pagination)
    const usersQuery = lastVisible
      ? query(
          usersRef,
          orderBy('fullName', 'asc'),
          limit(limitCount),
          startAfter(lastVisible),
        )
      : query(usersRef, orderBy('fullName', 'asc'), limit(limitCount));

    const querySnapshot = await getDocs(usersQuery);

    const employees: User[] = querySnapshot.docs.map(doc => {
      const data = doc.data() as UserDTO;
      return mapUserDTOToUser(data);
    });

    // Get the last visible document to continue pagination
    const lastVisibleSnapshot =
      querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      employees,
      lastVisibleSnapshot: lastVisibleSnapshot
        ? (lastVisibleSnapshot as DocumentSnapshot<User>)
        : null,
    };
  } catch (error) {
    console.error('Error fetching events:', error);
    throw new Error('Error fetching events');
  }
};

const getAllCompanyEmployees = async (companyId: string): Promise<User[]> => {
  try {
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
    const usersRef = collection(companyRef, FirestoreCollection.Users);

    const querySnapshot = await getDocs(usersRef);

    const employees: User[] = querySnapshot.docs.map(doc => {
      const data = doc.data() as UserDTO;
      return mapUserDTOToUser(data);
    });

    return employees.filter(employee => employee.email !== 'emil@hubbl.co');
  } catch (error) {
    console.error('Error fetching company employees:', error);
    throw new Error('Error fetching company employees');
  }
};

const addHRIStoCompany = async (companyId: string, hris: HRIS) => {
  const companyPromise = new Promise<void>(async (resolve, reject) => {
    try {
      const companyRef = doc(
        firestore,
        FirestoreCollection.Companies,
        companyId,
      );

      await updateDoc(companyRef, {'settings.connectedHRIS': hris});

      resolve();
    } catch (error) {
      reject(error);
    }
  });
  return companyPromise;
};

const createCompany = async (
  dataProps: CreateCompanyFromGetStartedWebDataProps,
) => {
  const callableFunc = httpsCallable(
    firebaseFunctions,
    'onCompanyCreatedFromGetStartedWeb',
  );
  return callableFunc(dataProps)
    .then(result => {
      console.log(result.data);
      return result.data as {pincode: string};
    })
    .catch(error => {
      console.log(`error: ${JSON.stringify(error)}`);
      throw error;
    });
};

const createCompanyForAuthenticatedUser = async (data: {
  companyName: string;
}): Promise<{companyId: string}> => {
  const callableFunc = httpsCallable<
    {companyName: string},
    {companyId: string}
  >(firebaseFunctions, 'onCreateCompanyForAuthenticatedUser');

  const result = await callableFunc(data);
  return result.data;
};

//TODO: Are we exposing company data here?
const getCompanyByDomain = async (
  userEmail: string,
): Promise<Company | undefined> => {
  const emailDomain = userEmail.split('@')[1];

  return new Promise<Company | undefined>(async (resolve, _) => {
    const companiesRef = collection(firestore, FirestoreCollection.Companies);
    const q = query(
      companiesRef,
      where('settings.restrictedMailDomains', 'array-contains', emailDomain),
    );

    const querySnapshot = await getDocs(q);

    const companies: Company[] = querySnapshot.docs.map(doc =>
      mapCompanyDTOToCompany(doc.data() as CompanyDTO),
    );

    if (companies.length === 1) {
      resolve(companies[0]);
    } else {
      resolve(undefined);
    }
  });
};

const updateSubCompanySlackChannel = async (
  companyId: string,
  locationId: string,
  subCompanyId: string,
  slackWebhookUrl: string,
): Promise<void> => {
  try {
    // Update organizational metadata directly via Firestore
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
    const updatePath = `organizationalMetadata.${locationId}.subCompanies.${subCompanyId}.slackWebhookUrl`;

    await updateDoc(companyRef, {
      [updatePath]: slackWebhookUrl || null, // Store null if empty string
    });
  } catch (error) {
    console.error('Error updating sub-company Slack channel:', error);
    throw error;
  }
};

const updateGeneralSlackChannel = async (
  companyId: string,
  webhookUrl: string,
): Promise<void> => {
  try {
    // Update company settings directly via Firestore
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);

    await updateDoc(companyRef, {
      'settings.slackIntegration.webhookUrl': webhookUrl || null, // Store null if empty string
    });
  } catch (error) {
    console.error('Error updating general Slack channel:', error);
    throw error;
  }
};

const updateSubCompanyTeamsChannel = async (
  companyId: string,
  locationId: string,
  subCompanyId: string,
  teamsWebhookUrl: string,
): Promise<void> => {
  try {
    // Update organizational metadata directly via Firestore
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
    const updatePath = `organizationalMetadata.${locationId}.subCompanies.${subCompanyId}.teamsWebhookUrl`;

    await updateDoc(companyRef, {
      [updatePath]: teamsWebhookUrl || null, // Store null if empty string
    });
  } catch (error) {
    console.error('Error updating sub-company Teams channel:', error);
    throw error;
  }
};

const updateGeneralTeamsChannel = async (
  companyId: string,
  webhookUrl: string,
): Promise<void> => {
  try {
    // Update company settings directly via Firestore
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);

    await updateDoc(companyRef, {
      'settings.teamsIntegration.webhookUrl': webhookUrl || null, // Store null if empty string
    });
  } catch (error) {
    console.error('Error updating general Teams channel:', error);
    throw error;
  }
};

const updateIntegrationPlatform = async (
  companyId: string,
  platform: IntegrationPlatform,
): Promise<void> => {
  try {
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);

    await updateDoc(companyRef, {
      'settings.integrationPlatform': platform,
    });
  } catch (error) {
    console.error('Error updating integration platform:', error);
    throw error;
  }
};

const deleteIntegration = async (companyId: string): Promise<void> => {
  try {
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);

    // Get current company data to check for organizational metadata
    const companySnapshot = await getDoc(companyRef);
    if (!companySnapshot.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companySnapshot.data();

    // Split into two separate updates to comply with Firestore security rules
    // Rules only allow updating settings OR organizationalMetadata, not both

    // Update 1: Clear settings (integrationPlatform and webhook URLs)
    const settingsUpdate: Record<string, null> = {
      'settings.integrationPlatform': null,
      'settings.slackIntegration.webhookUrl': null,
      'settings.teamsIntegration.webhookUrl': null,
    };

    // Update 2: Clear organizational metadata webhooks
    const orgMetadataUpdate: Record<string, null> = {};
    if (companyData.organizationalMetadata) {
      Object.entries(companyData.organizationalMetadata).forEach(
        ([locationId, location]: [string, any]) => {
          if (location.subCompanies) {
            Object.keys(location.subCompanies).forEach(subCompanyId => {
              orgMetadataUpdate[
                `organizationalMetadata.${locationId}.subCompanies.${subCompanyId}.slackWebhookUrl`
              ] = null;
              orgMetadataUpdate[
                `organizationalMetadata.${locationId}.subCompanies.${subCompanyId}.teamsWebhookUrl`
              ] = null;
            });
          }
        },
      );
    }

    // Execute both updates simultaneously
    const promises = [updateDoc(companyRef, settingsUpdate)];

    // Only add organizational metadata update if there are webhooks to clear
    if (Object.keys(orgMetadataUpdate).length > 0) {
      promises.push(updateDoc(companyRef, orgMetadataUpdate));
    }

    await Promise.all(promises);
  } catch (error) {
    console.error('Error deleting integration:', error);
    throw error;
  }
};

const addCompanyDepartment = async (
  companyId: string,
  newDepartment: Department,
): Promise<void> => {
  try {
    const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
    await updateDoc(companyRef, {
      departments: arrayUnion(newDepartment),
    });
  } catch (error) {
    console.error('Error adding company department:', error);
    throw new Error('Error adding company department');
  }
};

/**
 * Gets the allowed auth methods for a company by email domain.
 * Returns the company and its allowed auth methods (if multi-auth is enabled).
 */
const getCompanyAuthMethodsByEmail = async (
  userEmail: string,
): Promise<{
  company: Company | null;
  allowedAuthMethods: AllowedAuthMethod[];
  hasMultiAuth: boolean;
}> => {
  const emailDomain = userEmail.split('@')[1];

  const companiesRef = collection(firestore, FirestoreCollection.Companies);
  const q = query(
    companiesRef,
    where('settings.restrictedMailDomains', 'array-contains', emailDomain),
  );

  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return {company: null, allowedAuthMethods: [], hasMultiAuth: false};
  }

  // Take the first matching company
  const companyDoc = querySnapshot.docs[0];
  const companyData = companyDoc.data() as CompanyDTO;
  const company = mapCompanyDTOToCompany(companyData);

  // Get allowed auth methods from company settings
  const settings = companyData.settings as CompanySettings | undefined;
  const allowedAuthMethods = settings?.allowedAuthMethods || [];

  // Check if multi-auth is enabled (has more than one auth method)
  const hasMultiAuth =
    allowedAuthMethods.length > 1 ||
    (allowedAuthMethods.includes('sso') &&
      allowedAuthMethods.includes('google'));

  return {company, allowedAuthMethods, hasMultiAuth};
};

export interface EmployeeChanges {
  added: User[];
  modified: User[];
  removed: string[];
}

/**
 * Real-time listener for employee changes in a company.
 * Returns an unsubscribe function to stop listening.
 */
const onEmployeesChange = (
  companyId: string,
  callback: (changes: EmployeeChanges) => void,
): Unsubscribe => {
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const usersRef = collection(companyRef, FirestoreCollection.Users);

  return onSnapshot(usersRef, snapshot => {
    const added: User[] = [];
    const modified: User[] = [];
    const removed: string[] = [];

    snapshot.docChanges().forEach(change => {
      if (change.type === 'added') {
        added.push(mapUserDTOToUser(change.doc.data() as UserDTO));
      } else if (change.type === 'modified') {
        modified.push(mapUserDTOToUser(change.doc.data() as UserDTO));
      } else if (change.type === 'removed') {
        removed.push(change.doc.id);
      }
    });

    callback({added, modified, removed});
  });
};

export const company = {
  getCompanyEmployees,
  getAllCompanyEmployees,
  onEmployeesChange,
  addHRIStoCompany,
  getLoggedInUserCompany,
  createCompany,
  createCompanyForAuthenticatedUser,
  getCompanyByDomain,
  getCompanyAuthMethodsByEmail,
  updateSubCompanySlackChannel,
  updateGeneralSlackChannel,
  updateSubCompanyTeamsChannel,
  updateGeneralTeamsChannel,
  updateIntegrationPlatform,
  deleteIntegration,
  addCompanyDepartment,
};
